import type { ReactNode } from 'react';

export type StorageHierarchyLevel = 'shelf' | 'box' | 'row' | 'batch';
export type StorageGroupMode = StorageHierarchyLevel | 'none';

export const STORAGE_GROUP_MODES: readonly StorageGroupMode[] = [
  'shelf',
  'box',
  'row',
  'batch',
  'none',
];

export interface StorageHierarchySegment {
  level: StorageHierarchyLevel;
  key: string;
  label: string;
}

export interface StorageDisplayGroup<T> extends StorageHierarchySegment {
  nodeKey: string;
  count: number;
  items: T[];
}

export function storageGroupModeLabel(mode: StorageGroupMode): string {
  switch (mode) {
    case 'shelf':
      return 'Shelf';
    case 'box':
      return 'Box';
    case 'row':
      return 'Row';
    case 'batch':
      return 'Batch';
    case 'none':
      return 'None';
  }
}

function segmentNodeKey(path: readonly StorageHierarchySegment[], segmentIndex: number): string {
  return path
    .slice(0, segmentIndex + 1)
    .map((segment) => `${segment.level}:${segment.key}`)
    .join('/');
}

export function buildStorageGroups<T>(
  items: readonly T[],
  pathFor: (item: T) => readonly StorageHierarchySegment[],
  mode: Exclude<StorageGroupMode, 'none'>,
): StorageDisplayGroup<T>[] {
  const groups = new Map<string, StorageDisplayGroup<T>>();
  for (const item of items) {
    const path = pathFor(item);
    const segmentIndex = path.findIndex((segment) => segment.level === mode);
    const segment = segmentIndex >= 0 ? path[segmentIndex] : path[path.length - 1];
    const fallback = segment === undefined;
    const nodeKey = fallback
      ? `${mode}:unavailable`
      : segmentIndex >= 0
        ? segmentNodeKey(path, segmentIndex)
        : `${mode}:fallback:${segment.key}`;
    const existing = groups.get(nodeKey);
    if (existing === undefined) {
      groups.set(nodeKey, {
        level: mode,
        key: fallback ? 'unavailable' : segment.key,
        label: fallback ? 'Storage location unavailable' : segment.label,
        nodeKey,
        count: 1,
        items: [item],
      });
    } else {
      existing.count += 1;
      existing.items.push(item);
    }
  }
  return [...groups.values()];
}

export function storageLocationBreadcrumb(
  path: readonly StorageHierarchySegment[],
  groupedBy: StorageGroupMode,
): string {
  return path
    .filter((segment) => segment.level !== 'batch' && segment.level !== groupedBy)
    .map((segment) => segment.label)
    .join(' › ');
}

export function StorageGroupingControl({
  value,
  onChange,
}: {
  value: StorageGroupMode;
  onChange: (mode: StorageGroupMode) => void;
}): JSX.Element {
  return (
    <label className="tcvs-storage-grouping-control">
      <span>Group by</span>
      <select
        aria-label="Group by storage location"
        value={value}
        onChange={(event) => onChange(event.currentTarget.value as StorageGroupMode)}
      >
        {STORAGE_GROUP_MODES.map((mode) => (
          <option key={mode} value={mode}>
            {storageGroupModeLabel(mode)}
          </option>
        ))}
      </select>
    </label>
  );
}

interface CollapsibleStorageGroupsProps<T> {
  groups: readonly StorageDisplayGroup<T>[];
  expanded: ReadonlySet<string>;
  onToggle: (nodeKey: string) => void;
  renderItems: (items: readonly T[]) => ReactNode;
  showHeaders?: boolean;
  countLabel?: (group: StorageDisplayGroup<T>) => ReactNode;
}

export function CollapsibleStorageGroups<T>({
  groups,
  expanded,
  onToggle,
  renderItems,
  showHeaders = true,
  countLabel,
}: CollapsibleStorageGroupsProps<T>): JSX.Element {
  if (!showHeaders) {
    return (
      <div className="tcvs-storage-groups">
        {groups.map((group) => (
          <div key={group.nodeKey}>{renderItems(group.items)}</div>
        ))}
      </div>
    );
  }
  return (
    <div className="tcvs-storage-groups">
      {groups.map((group) => {
        const isExpanded = expanded.has(group.nodeKey);
        return (
          <section key={group.nodeKey} className="tcvs-storage-group">
            <button
              type="button"
              className="tcvs-storage-group-toggle"
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${group.label}`}
              onClick={() => onToggle(group.nodeKey)}
            >
              <span aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
              <span>{group.label}</span>
              <span className="tcvs-storage-group-count">{countLabel?.(group) ?? group.count}</span>
            </button>
            {isExpanded ? (
              <div className="tcvs-storage-group-items">{renderItems(group.items)}</div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
